import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import { FormSelect } from "@/components/Base/Form";
import { Dialog } from "@/components/Base/Headless";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import PageLoadingOverlay from "@/components/PageLoadingOverlay";
import { getToken } from "@/utils/auth";

type Team = { id: string; name: string; status?: boolean };
type Distributor = { id: string; name: string; supplyTo?: string[] };
type Row = {
  productId: string;
  product: string;
  sku: string | null;
  productType?: string | null;
  opening: number;
  totalIn: number;
  totalOut: number;
  closing: number;
  inByColumn: Record<string, number>;
  outByColumn: Record<string, number>;
};
type MovementColumn = {
  key: string;
  date: string;
  ownerName: string;
};
const PRODUCT_TYPE_ORDER = ["Cigarette", "Device", "Refills", "Accessories"] as const;

const FILTERS_STORAGE_KEY = "masterStockData.filters";

type StoredFilters = { dateRange?: string; distributorId?: string; teamId?: string };

/** Reads the last-remembered Date Range / Team filter from localStorage (returns null if unset/invalid). */
function loadStoredFilters(): StoredFilters | null {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as StoredFilters;
  } catch {
    return null;
  }
}

function saveStoredFilters(filters: StoredFilters) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
}

function dateToYMD(v: Date) {
  return v.toISOString().slice(0, 10);
}

function formatDateHeading(ymd: string) {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
  }).format(d);
}

function intFmt(v: number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function renderIntCell(v: number | null | undefined) {
  return v === null || v === undefined ? "-" : intFmt(v);
}

function parseDateRangeInput(value: string): { startDate: string; endDate: string } | null {
  const m = value.trim().match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return { startDate: m[1], endDate: m[2] };
}

function safeSheetName(name: string, used: Set<string>): string {
  const base = name.replace(/[\\/*?:[\]]/g, " ").trim().slice(0, 28) || "Sheet";
  let candidate = base;
  let n = 2;
  while (used.has(candidate)) {
    const suffix = ` ${n}`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    n += 1;
  }
  used.add(candidate);
  return candidate;
}

function cellQty(v: number | null | undefined): number | string {
  return v === null || v === undefined ? "" : v;
}

function formatDateHeadingExcel(ymd: string) {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

function productTypeLabel(row: Row): string {
  return (row.productType || "Other").trim() || "Other";
}

function movementSubHeader(c: MovementColumn): string {
  return `${c.ownerName} (${formatDateHeadingExcel(c.date)})`;
}

type TotalsRow = {
  opening: number;
  totalIn: number;
  totalOut: number;
  closing: number;
  inByColumn: Record<string, number>;
  outByColumn: Record<string, number>;
};

type SheetBuildResult = {
  aoa: (string | number)[][];
  merges: XLSX.Range[];
};

function applySheetBuild(ws: XLSX.WorkSheet, build: SheetBuildResult) {
  if (build.merges.length) {
    ws["!merges"] = [...(ws["!merges"] || []), ...build.merges];
  }
}

function buildMasterStockSheetAoa(
  rows: Row[],
  inColumns: MovementColumn[],
  outColumns: MovementColumn[],
  meta: {
    startDate: string;
    endDate: string;
    distributorLabel: string;
    teamLabel: string;
    productTypesLabel: string;
  },
  totals: TotalsRow
): SheetBuildResult {
  const aoa: (string | number)[][] = [
    ["Master Stock Data Report"],
    ["Date Range", `${meta.startDate} - ${meta.endDate}`],
    ["Distributor", meta.distributorLabel],
    ["Team", meta.teamLabel],
    ["Product Types", meta.productTypesLabel],
    [],
  ];

  const leftHeaders = ["Product", "Product Type", "SKU", "Opening"];
  const rightHeaders = ["Closing"];
  const inSubHeaders = [...inColumns.map(movementSubHeader), "Total In"];
  const outSubHeaders = [...outColumns.map(movementSubHeader), "Total Out"];
  const headerTopRow = aoa.length;
  const headerSubRow = headerTopRow + 1;

  const topRow: (string | number)[] = [...leftHeaders];
  const subRow: (string | number)[] = leftHeaders.map(() => "");
  topRow.push("Stock IN", ...Array(Math.max(inSubHeaders.length - 1, 0)).fill(""));
  subRow.push(...inSubHeaders);
  topRow.push("Stock OUT", ...Array(Math.max(outSubHeaders.length - 1, 0)).fill(""));
  subRow.push(...outSubHeaders);
  topRow.push(...rightHeaders);
  subRow.push(...rightHeaders.map(() => ""));
  aoa.push(topRow, subRow);

  const merges: XLSX.Range[] = [];
  for (let c = 0; c < leftHeaders.length; c++) {
    merges.push({ s: { r: headerTopRow, c }, e: { r: headerSubRow, c } });
  }
  let col = leftHeaders.length;
  if (inSubHeaders.length > 0) {
    merges.push({
      s: { r: headerTopRow, c: col },
      e: { r: headerTopRow, c: col + inSubHeaders.length - 1 },
    });
    col += inSubHeaders.length;
  }
  if (outSubHeaders.length > 0) {
    merges.push({
      s: { r: headerTopRow, c: col },
      e: { r: headerTopRow, c: col + outSubHeaders.length - 1 },
    });
    col += outSubHeaders.length;
  }
  for (let i = 0; i < rightHeaders.length; i++) {
    const c = col + i;
    merges.push({ s: { r: headerTopRow, c }, e: { r: headerSubRow, c } });
  }

  for (const r of rows) {
    const row: (string | number)[] = [r.product, productTypeLabel(r), r.sku || "-", Number(r.opening ?? 0)];
    for (const c of inColumns) row.push(cellQty(r.inByColumn?.[c.key]));
    row.push(Number(r.totalIn ?? 0));
    for (const c of outColumns) row.push(cellQty(r.outByColumn?.[c.key]));
    row.push(Number(r.totalOut ?? 0), Number(r.closing ?? 0));
    aoa.push(row);
  }

  const totalRow: (string | number)[] = ["Total", "-", "-", totals.opening];
  for (const c of inColumns) totalRow.push(totals.inByColumn[c.key] ?? 0);
  totalRow.push(totals.totalIn);
  for (const c of outColumns) totalRow.push(totals.outByColumn[c.key] ?? 0);
  totalRow.push(totals.totalOut, totals.closing);
  aoa.push(totalRow);

  return { aoa, merges };
}

/**
 * Renders the (potentially very large, unvirtualized) stock movement table.
 * Memoized so that unrelated state changes in the parent (e.g. opening the
 * Export Report dialog) don't force React to re-render/reconcile this whole
 * tree — that reconciliation work was what made the dialog feel slow to appear.
 */
const StockDataTable = memo(function StockDataTable({
  loading,
  groupedRows,
  expandedTypeNames,
  onToggleType,
  inColumns,
  outColumns,
  buildTotals,
  emptyHint,
}: {
  loading: boolean;
  groupedRows: [string, Row[]][];
  expandedTypeNames: Set<string>;
  onToggleType: (typeName: string) => void;
  inColumns: MovementColumn[];
  outColumns: MovementColumn[];
  buildTotals: (list: Row[]) => TotalsRow;
  emptyHint?: string;
}) {
  if (loading) {
    return (
      <div className="flex flex-col box box--stacked">
        <div className="px-5 py-6 text-slate-500 text-sm flex items-center gap-2">
          <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  if (groupedRows.length === 0) {
    return (
      <div className="flex flex-col box box--stacked">
        <div className="px-5 py-6 text-slate-500 text-sm">
          {emptyHint || "No stock movement data found for selected filter."}
        </div>
      </div>
    );
  }

  return (
    <>
      {groupedRows.map(([typeName, typeRows]) => {
        const expanded = expandedTypeNames.has(typeName);
        const totals = buildTotals(typeRows);
        return (
          <div key={typeName} className="flex flex-col box box--stacked">
            <div className="px-5 py-3 border-b border-slate-200/70 dark:border-darkmode-500">
              <button
                type="button"
                className="w-full flex items-center gap-2 text-left"
                onClick={() => onToggleType(typeName)}
              >
                <Lucide icon={expanded ? "ChevronDown" : "ChevronRight"} className="w-4 h-4 text-slate-500" />
                <span className="font-medium">{typeName}</span>
                <span className="text-xs text-slate-500">({typeRows.length})</span>
              </button>
            </div>
            {expanded ? (
              <div className="max-w-full overflow-x-auto px-4 pb-4">
                <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
                  <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                    <Table.Tr>
                      <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] align-middle">
                        Product
                      </Table.Td>
                      <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[220px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] align-middle">
                        SKU
                      </Table.Td>
                      <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[350px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] text-right align-middle">
                        Opening
                      </Table.Td>
                      <Table.Td colSpan={inColumns.length + 1} className="py-2 px-2 font-medium text-center bg-success/20 dark:bg-success/20">
                        Stock IN
                      </Table.Td>
                      <Table.Td colSpan={outColumns.length + 1} className="py-2 px-2 font-medium text-center bg-warning/20 dark:bg-warning/20 border-l border-slate-200/80 dark:border-darkmode-400">
                        Stock OUT
                      </Table.Td>
                      <Table.Td rowSpan={2} className="py-2 px-2 font-medium bg-slate-50 dark:bg-darkmode-700 border-l border-slate-200/80 dark:border-darkmode-400 min-w-[95px] text-right align-middle">
                        Closing
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      {inColumns.map((c) => (
                        <Table.Td key={`in-${c.key}`} className="py-2 px-2 text-center font-medium bg-success/10 dark:bg-success/10 min-w-[92px] whitespace-normal leading-tight">
                          <div className="text-[10px] text-slate-500">{c.ownerName}</div>
                          <div>{formatDateHeading(c.date)}</div>
                        </Table.Td>
                      ))}
                      <Table.Td className="py-2 px-2 text-center font-medium bg-success/30 dark:bg-success/30 min-w-[74px] whitespace-nowrap">
                        Total In
                      </Table.Td>
                      {outColumns.map((c) => (
                        <Table.Td key={`out-${c.key}`} className="py-2 px-2 text-center font-medium bg-warning/10 dark:bg-warning/10 min-w-[92px] whitespace-normal leading-tight border-l border-slate-200/80 dark:border-darkmode-400 first:border-l-0">
                          <div className="text-[10px] text-slate-500">{c.ownerName}</div>
                          <div>{formatDateHeading(c.date)}</div>
                        </Table.Td>
                      ))}
                      <Table.Td className="py-2 px-2 text-center font-medium bg-warning/30 dark:bg-warning/30 min-w-[74px] whitespace-nowrap">
                        Total Out
                      </Table.Td>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {typeRows.map((r) => (
                      <Table.Tr key={r.productId} className="[&_td]:border-b [&_td]:border-slate-200/60">
                        <Table.Td className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate">
                          {r.product}
                        </Table.Td>
                        <Table.Td className="py-2 px-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] truncate">
                          {r.sku || "-"}
                        </Table.Td>
                        <Table.Td className="py-2 px-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-50 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                          {intFmt(r.opening)}
                        </Table.Td>
                        {inColumns.map((c) => (
                          <Table.Td key={`${r.productId}-in-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-success/10 dark:bg-success/10 min-w-[92px] whitespace-nowrap">
                            {renderIntCell(r.inByColumn?.[c.key])}
                          </Table.Td>
                        ))}
                        <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/30 dark:bg-success/30 min-w-[74px] whitespace-nowrap">
                          {intFmt(r.totalIn)}
                        </Table.Td>
                        {outColumns.map((c) => (
                          <Table.Td key={`${r.productId}-out-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-warning/10 dark:bg-warning/10 min-w-[92px] whitespace-nowrap border-l border-slate-200/80 dark:border-darkmode-400 first:border-l-0">
                            {renderIntCell(r.outByColumn?.[c.key])}
                          </Table.Td>
                        ))}
                        <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/30 dark:bg-warning/30 min-w-[74px] whitespace-nowrap">
                          {intFmt(r.totalOut)}
                        </Table.Td>
                        <Table.Td className="py-2 px-2 text-right tabular-nums bg-slate-50/70 dark:bg-darkmode-700/40 border-l border-slate-200/80 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                          {intFmt(r.closing)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {typeRows.length > 0 ? (
                      <Table.Tr className="font-medium [&_td]:border-b [&_td]:border-slate-300/80 dark:[&_td]:border-darkmode-400">
                        <Table.Td className="py-2 px-2 bg-slate-100 dark:bg-darkmode-700 sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] whitespace-nowrap">
                          Total
                        </Table.Td>
                        <Table.Td className="py-2 px-2 bg-slate-100 dark:bg-darkmode-700 sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] whitespace-nowrap">
                          -
                        </Table.Td>
                        <Table.Td className="py-2 px-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                          {intFmt(totals.opening)}
                        </Table.Td>
                        {inColumns.map((c) => (
                          <Table.Td key={`total-in-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-success/20 dark:bg-success/20 min-w-[92px] whitespace-nowrap">
                            {intFmt(totals.inByColumn[c.key] ?? 0)}
                          </Table.Td>
                        ))}
                        <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/40 dark:bg-success/40 min-w-[74px] whitespace-nowrap">
                          {intFmt(totals.totalIn)}
                        </Table.Td>
                        {outColumns.map((c) => (
                          <Table.Td key={`total-out-${c.key}`} className="py-2 px-2 text-center tabular-nums bg-warning/20 dark:bg-warning/20 min-w-[92px] whitespace-nowrap border-l border-slate-200/80 dark:border-darkmode-400 first:border-l-0">
                            {intFmt(totals.outByColumn[c.key] ?? 0)}
                          </Table.Td>
                        ))}
                        <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/40 dark:bg-warning/40 min-w-[74px] whitespace-nowrap">
                          {intFmt(totals.totalOut)}
                        </Table.Td>
                        <Table.Td className="py-2 px-2 text-right tabular-nums bg-slate-200/70 dark:bg-darkmode-700 border-l border-slate-200/80 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                          {intFmt(totals.closing)}
                        </Table.Td>
                      </Table.Tr>
                    ) : null}
                  </Table.Tbody>
                </Table>
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
});

export default function MasterStockDataPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const defaultStart = dateToYMD(monthStart);
  const defaultEnd = dateToYMD(today);
  const defaultDateRange = `${defaultStart} - ${defaultEnd}`;

  const [dateRange, setDateRange] = useState(() => {
    const stored = loadStoredFilters();
    const storedRange = stored?.dateRange?.trim();
    return storedRange && parseDateRangeInput(storedRange) ? storedRange : defaultDateRange;
  });
  const [distributorId, setDistributorId] = useState(() => loadStoredFilters()?.distributorId?.trim() || "");
  const [teamId, setTeamId] = useState(() => loadStoredFilters()?.teamId?.trim() || "");

  const [distributors, setDistributors] = useState<Distributor[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [baseDataLoaded, setBaseDataLoaded] = useState(false);
  const [inColumns, setInColumns] = useState<MovementColumn[]>([]);
  const [outColumns, setOutColumns] = useState<MovementColumn[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  // Filters that produced the currently displayed/exported dataset (last successful Apply).
  const [appliedFilters, setAppliedFilters] = useState<{
    startDate: string;
    endDate: string;
    distributorId: string;
    teamId: string;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedTypeNames, setExpandedTypeNames] = useState<Set<string>>(new Set());
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportSelectedTypes, setExportSelectedTypes] = useState<Set<string>>(new Set());
  const [exportError, setExportError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [dRes, tRes] = await Promise.all([
          axios.get(`${apiUrl}/distributors?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${apiUrl}/teams?pageSize=500`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setDistributors(Array.isArray(dRes.data?.data) ? dRes.data.data : []);
        const list: Team[] = Array.isArray(tRes.data?.data) ? tRes.data.data : [];
        setTeams(list.filter((t) => t.status !== false).sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        // keep page usable without distributors/teams list
      } finally {
        setBaseDataLoaded(true);
      }
    })();
  }, [apiUrl, token]);

  /** When a distributor is selected, only teams in that distributor's supplyTo (Stocks Supply To). */
  const teamsForFilter = useMemo(() => {
    if (!distributorId) return [];
    const distributor = distributors.find((d) => d.id === distributorId);
    if (!distributor) return [];
    const supplyNames = new Set(
      (distributor.supplyTo || [])
        .map((name) => String(name).trim().toLowerCase())
        .filter(Boolean)
    );
    return teams.filter((t) => supplyNames.has((t.name || "").trim().toLowerCase()));
  }, [distributorId, distributors, teams]);

  useEffect(() => {
    if (!baseDataLoaded || !teamId) return;
    if (!teamsForFilter.some((t) => t.id === teamId)) setTeamId("");
  }, [baseDataLoaded, teamId, teamsForFilter]);

  const loadRequestIdRef = useRef(0);

  const loadData = async () => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setError("");
    // Clear previous result immediately so an older unfiltered table cannot linger
    // while the filtered request is in flight (or while the browser serves a 304).
    setInColumns([]);
    setOutColumns([]);
    setRows([]);
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      if (distributorId) params.set("distributorId", distributorId);
      if (teamId) params.set("teamId", teamId);
      // Bust HTTP caches — Express ETags were returning 304 and could leave stale UI.
      params.set("_", String(Date.now()));

      const res = await axios.get(`${apiUrl}/master-stock-data?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (requestId !== loadRequestIdRef.current) return;
      setInColumns(Array.isArray(res.data?.inColumns) ? res.data.inColumns : []);
      setOutColumns(Array.isArray(res.data?.outColumns) ? res.data.outColumns : []);
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setAppliedFilters({
        startDate: parsedRange.startDate,
        endDate: parsedRange.endDate,
        distributorId,
        teamId,
      });
    } catch (e: any) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(e?.response?.data?.message || "Failed to load Master Stock Data.");
      setInColumns([]);
      setOutColumns([]);
      setRows([]);
      setAppliedFilters(null);
    } finally {
      if (requestId === loadRequestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load + whenever Distributor / Team selection changes.
    // Date range still uses Apply Filter so typing/picking dates doesn't spam requests.
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributorId, teamId]);

  // Remember the last-used Date Range / Distributor / Team filter so it's restored on the next visit.
  useEffect(() => {
    saveStoredFilters({ dateRange, distributorId, teamId });
  }, [dateRange, distributorId, teamId]);

  const colCount = useMemo(
    () => 4 + inColumns.length + 1 + outColumns.length + 1 + 1,
    [inColumns.length, outColumns.length]
  );
  const groupedRows = useMemo(() => {
    const groups = new Map<string, Row[]>();
    for (const r of rows) {
      const typeName = (r.productType || "Other").trim() || "Other";
      const arr = groups.get(typeName) ?? [];
      arr.push(r);
      groups.set(typeName, arr);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      const ai = PRODUCT_TYPE_ORDER.indexOf(a[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      const bi = PRODUCT_TYPE_ORDER.indexOf(b[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [rows]);

  useEffect(() => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      for (const [typeName] of groupedRows) next.add(typeName);
      return next;
    });
  }, [groupedRows]);

  const toggleTypeExpanded = useCallback((typeName: string) => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      if (next.has(typeName)) next.delete(typeName);
      else next.add(typeName);
      return next;
    });
  }, []);

  const buildTotals = useCallback(
    (list: Row[]): TotalsRow => {
      const out: TotalsRow = {
        opening: 0,
        totalIn: 0,
        totalOut: 0,
        closing: 0,
        inByColumn: Object.fromEntries(inColumns.map((c) => [c.key, 0])) as Record<string, number>,
        outByColumn: Object.fromEntries(outColumns.map((c) => [c.key, 0])) as Record<string, number>,
      };
      for (const r of list) {
        out.opening += Number(r.opening ?? 0);
        out.totalIn += Number(r.totalIn ?? 0);
        out.totalOut += Number(r.totalOut ?? 0);
        out.closing += Number(r.closing ?? 0);
        for (const c of inColumns) {
          out.inByColumn[c.key] += Number(r.inByColumn?.[c.key] ?? 0);
        }
        for (const c of outColumns) {
          out.outByColumn[c.key] += Number(r.outByColumn?.[c.key] ?? 0);
        }
      }
      return out;
    },
    [inColumns, outColumns]
  );

  const availableExportTypes = useMemo(() => groupedRows.map(([typeName]) => typeName), [groupedRows]);

  const appliedDistributorLabel = useMemo(() => {
    if (!appliedFilters?.distributorId) return "All Distributors";
    return distributors.find((d) => d.id === appliedFilters.distributorId)?.name || "All Distributors";
  }, [appliedFilters, distributors]);

  const appliedTeamLabel = useMemo(() => {
    if (!appliedFilters?.teamId) return "All Teams";
    return teams.find((t) => t.id === appliedFilters.teamId)?.name || "All Teams";
  }, [appliedFilters, teams]);

  const emptyHint = useMemo(() => {
    if (!appliedFilters) return undefined;
    if (appliedFilters.distributorId) {
      const distName =
        distributors.find((d) => d.id === appliedFilters.distributorId)?.name || "selected distributor";
      return `No stock movement data found for distributor "${distName}" in the selected date range.`;
    }
    return "No stock movement data found for selected filter.";
  }, [appliedFilters, distributors]);

  const openExportModal = () => {
    if (rows.length === 0 || !appliedFilters) {
      setError("No data to export. Apply filter first.");
      return;
    }
    setExportError("");
    setExportSelectedTypes(new Set(availableExportTypes));
    setExportModalOpen(true);
  };

  const handleExportReport = () => {
    if (!appliedFilters) {
      setExportError("No applied filter data to export. Click Apply Filter first.");
      return;
    }
    if (exportSelectedTypes.size === 0) {
      setExportError("Select at least one product type.");
      return;
    }

    const selectedGroups = groupedRows.filter(([typeName]) => exportSelectedTypes.has(typeName));
    if (selectedGroups.length === 0) {
      setExportError("No data for selected product types.");
      return;
    }

    setExporting(true);
    setExportError("");
    try {
      const wb = XLSX.utils.book_new();
      const usedSheetNames = new Set<string>();
      const exportRows = selectedGroups.flatMap(([, typeRows]) => typeRows);
      const productTypesLabel = selectedGroups.map(([typeName]) => typeName).join(", ");
      const meta = {
        startDate: appliedFilters.startDate,
        endDate: appliedFilters.endDate,
        distributorLabel: appliedDistributorLabel,
        teamLabel: appliedTeamLabel,
        productTypesLabel,
      };
      const build = buildMasterStockSheetAoa(exportRows, inColumns, outColumns, meta, buildTotals(exportRows));
      const ws = XLSX.utils.aoa_to_sheet(build.aoa);
      applySheetBuild(ws, build);
      XLSX.utils.book_append_sheet(wb, ws, safeSheetName("Master Stock", usedSheetNames));

      const distSlug = appliedDistributorLabel
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase() || "all-distributors";
      const teamSlug = appliedTeamLabel.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "all-teams";
      const filename = `master-stock-data-${appliedFilters.startDate}-to-${appliedFilters.endDate}-${distSlug}-${teamSlug}.xlsx`;
      XLSX.writeFile(wb, filename);
      setExportModalOpen(false);
    } catch {
      setExportError("Failed to export report.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <PageLoadingOverlay show={loading} label="Loading master stock data…" />
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Master Stock Data</div>
            </div>

            <div className="flex flex-col gap-8 mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="flex flex-col p-5 sm:flex-row sm:items-end gap-3">
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.7fr)_minmax(0,0.7fr)_auto_auto] gap-3 w-full sm:w-auto">
                    <div className="sm:w-[420px]">
                      <div className="text-xs text-slate-500 mb-1">Date Range</div>
                      <div className="relative inline-flex w-full">
                        <Lucide
                          icon="Calendar"
                          className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                        />
                        <Litepicker
                          value={dateRange}
                          onChange={(e) => setDateRange((e.target?.value || "").trim())}
                          options={{
                            autoApply: true,
                            singleMode: false,
                            numberOfColumns: 2,
                            numberOfMonths: 2,
                            showWeekNumbers: true,
                            dropdowns: {
                              minYear: 2020,
                              maxYear: null,
                              months: true,
                              years: true,
                            },
                            format: "YYYY-MM-DD",
                          }}
                          className="w-full min-w-[220px] pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                        />
                      </div>
                    </div>
                    <div className="sm:w-[170px]">
                      <div className="text-xs text-slate-500 mb-1">Distributor</div>
                      <FormSelect
                        value={distributorId}
                        onChange={(e) => {
                          setDistributorId(e.target.value);
                          setTeamId("");
                        }}
                      >
                        <option value="">All Distributors</option>
                        {distributors.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </FormSelect>
                    </div>
                    <div className="sm:w-[170px]">
                      <div className="text-xs text-slate-500 mb-1">Team</div>
                      <FormSelect
                        value={teamId}
                        onChange={(e) => setTeamId(e.target.value)}
                        disabled={!distributorId}
                        title={!distributorId ? "Select a Distributor first" : undefined}
                      >
                        <option value="">All Teams</option>
                        {teamsForFilter.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </FormSelect>
                    </div>
                    <div className="sm:self-end">
                      <Button variant="primary" onClick={() => void loadData()} disabled={loading} className="whitespace-nowrap px-4">
                        Apply Filter
                      </Button>
                    </div>
                    <div className="sm:self-end">
                      <Button
                        variant="outline-secondary"
                        onClick={openExportModal}
                        disabled={loading || rows.length === 0}
                        className="whitespace-nowrap px-4"
                      >
                        <Lucide icon="Download" className="w-4 h-4 mr-2" />
                        Export Report
                      </Button>
                    </div>
                  </div>
                </div>

                {error ? <div className="px-5 pb-3 text-danger text-sm">{error}</div> : null}

              </div>

              <StockDataTable
                loading={loading}
                groupedRows={groupedRows}
                expandedTypeNames={expandedTypeNames}
                onToggleType={toggleTypeExpanded}
                inColumns={inColumns}
                outColumns={outColumns}
                buildTotals={buildTotals}
                emptyHint={emptyHint}
              />
            </div>
          </div>
        </div>
        <Dialog
          open={exportModalOpen}
          onClose={() => {
            if (exporting) return;
            setExportModalOpen(false);
            setExportError("");
          }}
        >
          <Dialog.Panel className="max-w-md">
            <Dialog.Title>
              <div className="font-medium">Export Report</div>
            </Dialog.Title>
            <Dialog.Description className="px-5 py-4 space-y-4">
              <div className="text-sm text-slate-600">
                Export the current filter result to Excel. All selected product categories go on one sheet, in the same layout as Team Data. Select one or more product types to include.
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline-secondary"
                  className="text-xs px-2 py-1"
                  onClick={() => setExportSelectedTypes(new Set(availableExportTypes))}
                  disabled={exporting}
                >
                  Select All
                </Button>
                <Button
                  variant="outline-secondary"
                  className="text-xs px-2 py-1"
                  onClick={() => setExportSelectedTypes(new Set())}
                  disabled={exporting}
                >
                  Clear All
                </Button>
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-200/80 rounded-md divide-y divide-slate-200/60">
                {availableExportTypes.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-slate-500">No product types in current result.</div>
                ) : (
                  availableExportTypes.map((typeName) => {
                    const count = groupedRows.find(([name]) => name === typeName)?.[1].length ?? 0;
                    const checked = exportSelectedTypes.has(typeName);
                    return (
                      <label
                        key={typeName}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-slate-300"
                          checked={checked}
                          disabled={exporting}
                          onChange={() => {
                            setExportSelectedTypes((prev) => {
                              const next = new Set(prev);
                              if (next.has(typeName)) next.delete(typeName);
                              else next.add(typeName);
                              return next;
                            });
                          }}
                        />
                        <span className="text-sm flex-1">{typeName}</span>
                        <span className="text-xs text-slate-500">{count} products</span>
                      </label>
                    );
                  })
                )}
              </div>
              {exportError ? <div className="text-sm text-danger">{exportError}</div> : null}
            </Dialog.Description>
            <Dialog.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setExportModalOpen(false);
                  setExportError("");
                }}
                disabled={exporting}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleExportReport} disabled={exporting || availableExportTypes.length === 0}>
                {exporting ? (
                  <>
                    <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Lucide icon="Download" className="w-4 h-4 mr-2" />
                    Download Excel
                  </>
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      </RoleGate>
    </ProtectedRoute>
  );
}

